/**
 * Per-tool discovery payload: three short questions rendered under every free tool, the
 * `/api/<apiId>` page that tool sells, and three sibling tools. The questions are the ONLY
 * body copy the tool pages carry, so they do double duty - visible answers plus the FAQPage
 * node in `lib/seo/toolSchema.ts`. One owner: never restate this text in a component.
 *
 * Budget: 100 words per slug across every question and answer, enforced by `toolSeo.test.ts`.
 * `apiId` must be a live platform id (see the pinned allowlist in that test); omit it when the
 * catalog has no matching platform page, as with Twitch.
 */

export interface ToolFaq {
  a: string;
  q: string;
}

export interface ToolSeo {
  apiId?: string;
  faq: readonly ToolFaq[];
  related: readonly string[];
}

/** Tools whose social card is bespoke art instead of a generated one. */
const TOOL_OG_ART: Readonly<Record<string, string>> = {
  'instagram-transcript': '/og/instagram-transcript.png',
  'tiktok-transcript': '/og/tiktok-transcript.png',
  'x-non-followers': '/og/x-non-followers.png',
  'youtube-transcript': '/og/youtube-transcript.png',
};

/** Public path of a tool's 1200x630 card; generated ones come from `brand/og/tools/render.ts`. */
export const toolOgImage = (slug: string): string => TOOL_OG_ART[slug] ?? `/og/tools/${slug}.png`;

export const TOOL_SEO: Readonly<Record<string, ToolSeo>> = {
  'social-transcript': {
    apiId: 'social',
    related: ['youtube-transcript', 'tiktok-transcript', 'instagram-transcript'],
    faq: [
      { q: 'Which platforms does it accept?', a: 'Public TikTok, YouTube, Instagram, Facebook, and X video links. Paste one URL and the tool picks the right source itself.' },
      { q: 'Do I need an account?', a: 'No. It is free and needs no login, just a short human check before each run.' },
      { q: 'What if a video has no speech?', a: 'You get an empty result with the sources that were checked, never an invented transcript.' },
    ],
  },
  'youtube-channel-transcripts': {
    apiId: 'youtube',
    related: ['youtube-transcript', 'youtube-comment-analyzer', 'social-transcript'],
    faq: [
      { q: 'What does it return?', a: 'The latest videos returned for a public channel, each with its available transcript, collected in one place.' },
      { q: 'Does it cover every video?', a: 'No. You get the sample the channel lookup returns, and the tool states how many rows it retrieved.' },
      { q: 'Can I run this over many channels?', a: 'Not here. The batch path is the YouTube API, billed per request in USD.' },
    ],
  },
  'profile-viewer': {
    apiId: 'social',
    related: ['username-checker', 'snapchat-story-viewer', 'tiktok-audience-analyzer'],
    faq: [
      { q: 'Which fields come back?', a: 'Handle, name, bio, follower and post counts, and the profile link, in the same shape on every platform.' },
      { q: 'Can it read private accounts?', a: 'No. Only public profile data is returned, and each result keeps a link to its public source.' },
      { q: 'Why normalize the shape?', a: 'So one set of field names works across networks instead of a different payload per platform.' },
    ],
  },
  'snapchat-story-viewer': {
    apiId: 'snapchat',
    related: ['profile-viewer', 'username-checker', 'tiktok-audience-analyzer'],
    faq: [
      { q: 'Can I view stories anonymously?', a: 'You see the public profile and current story metadata Snapchat exposes. Nothing private is retrieved and no account is needed.' },
      { q: 'What if there is no active story?', a: 'The result says so and keeps whatever public profile fields it did return.' },
      { q: 'Is the creator notified?', a: 'No. Nothing is opened inside Snapchat and no view is registered.' },
    ],
  },
  'tiktok-audience-analyzer': {
    apiId: 'tiktok',
    related: ['profile-viewer', 'engagement-rate-calculator', 'tiktok-transcript'],
    faq: [
      { q: 'What does audience mean here?', a: 'The latest public country distribution returned for the account, not the private creator analytics TikTok shows owners.' },
      { q: 'How current is the sample?', a: 'Every run prints its retrieval time, so you can see exactly when the distribution was taken.' },
      { q: 'Can I check an account I do not own?', a: 'Yes. Only public data is read, so no ownership or login is required.' },
    ],
  },
  'username-checker': {
    apiId: 'social',
    related: ['profile-viewer', 'snapchat-story-viewer', 'instagram-non-followers'],
    faq: [
      { q: 'Does available mean I can register it?', a: 'It means no public profile was found on that network. Reserved names and platform rules still apply.' },
      { q: 'Which networks are checked?', a: 'The supported social networks in the catalog, each reported with a link to the profile that was found.' },
      { q: 'How fast is a full sweep?', a: 'All supported networks are checked in one run rather than one tab at a time.' },
    ],
  },
  'instagram-non-followers': {
    apiId: 'instagram',
    related: ['x-non-followers', 'profile-viewer', 'engagement-rate-calculator'],
    faq: [
      { q: 'Do my files get uploaded?', a: 'No. The comparison runs entirely in your browser and the export files never leave the page.' },
      { q: 'Where do I get the files?', a: 'Request your data from Instagram, then load the followers and following files out of that export.' },
      { q: 'Does Instagram see this?', a: 'No. Nothing is sent to Instagram and no password or login is involved.' },
    ],
  },
  'engagement-rate-calculator': {
    apiId: 'instagram',
    related: ['creator-earnings-calculator', 'youtube-shorts-earnings', 'tiktok-audience-analyzer'],
    faq: [
      { q: 'Which formula is used?', a: 'Interactions divided by followers, and by reach when you supply it. Every input stays visible so you can check the math.' },
      { q: 'What counts as a good rate?', a: 'It moves with platform and audience size. The tool reports your number and does not grade it.' },
      { q: 'Which platforms does it fit?', a: 'Instagram, TikTok, and YouTube samples, since the inputs are yours rather than platform specific.' },
    ],
  },
  'creator-earnings-calculator': {
    apiId: 'instagram',
    related: ['youtube-shorts-earnings', 'twitch-earnings-calculator', 'engagement-rate-calculator'],
    faq: [
      { q: 'How is the estimate built?', a: 'From audience size, views, and engagement, with every rate assumption editable so the range is yours, not a hidden model.' },
      { q: 'Is this a real quote?', a: 'No. It is a modelled range to negotiate against, not a price any brand has agreed.' },
      { q: 'Why are the assumptions editable?', a: 'Because rates move by niche and market, and a fixed multiplier would hide that.' },
    ],
  },
  'youtube-shorts-earnings': {
    apiId: 'youtube',
    related: ['creator-earnings-calculator', 'youtube-comment-analyzer', 'twitch-earnings-calculator'],
    faq: [
      { q: 'Where does the view data come from?', a: 'The latest public Shorts on the channel, with the observed posting cadence shown beside the estimate.' },
      { q: 'Can I change the RPM?', a: 'Yes. RPM is an editable input, because Shorts payouts move with niche, country, and season.' },
      { q: 'Does it need channel access?', a: 'No. It reads public Shorts, so you can model any channel including a competitor.' },
    ],
  },
  'twitch-earnings-calculator': {
    related: ['creator-earnings-calculator', 'youtube-shorts-earnings', 'engagement-rate-calculator'],
    faq: [
      { q: 'What do I need to enter?', a: 'Your own dashboard totals for subscriptions, bits, viewers, and stream hours. Nothing is read from your Twitch account.' },
      { q: 'Which revenue split does it assume?', a: 'The subscription and bit assumptions are visible and editable, so you can match your own contract terms.' },
      { q: 'Does it cover sponsorships?', a: 'No. It models platform income only; brand deals sit outside the numbers Twitch reports.' },
    ],
  },
  'youtube-comment-analyzer': {
    apiId: 'youtube',
    related: ['youtube-channel-transcripts', 'brand-mention-checker', 'youtube-transcript'],
    faq: [
      { q: 'What does it measure?', a: 'Question and term counts, ranked threads, and themes with sentiment, each cited back to the comment it came from.' },
      { q: 'How many comments are read?', a: 'The public sample the request returns. The tool prints that row count rather than implying full coverage.' },
      { q: 'Why does every theme carry a link?', a: 'So a generated claim can always be checked against the comment that produced it.' },
    ],
  },
  'reddit-research': {
    apiId: 'reddit',
    related: ['brand-mention-checker', 'switch-signals', 'reddit-marketing'],
    faq: [
      { q: 'Can I control sort and timeframe?', a: 'Yes. Both are explicit inputs, so you can see exactly which slice of Reddit was searched.' },
      { q: 'Are the results linked to sources?', a: 'Every row keeps its public Reddit permalink, so nothing has to be taken on trust.' },
      { q: 'Do I need a Reddit account?', a: 'No. Only public posts are searched, with no login and no app credentials.' },
    ],
  },
  'reddit-marketing': {
    apiId: 'reddit',
    related: ['reddit-research', 'switch-signals', 'reddit-seo-finder'],
    faq: [
      { q: 'What do I get back?', a: 'A plan built from retrieved Reddit rows, with each recommendation cited to the thread that supports it.' },
      { q: 'Will it post for me?', a: 'No. It drafts direction from real threads; subreddit rules and disclosure stay yours to follow.' },
      { q: 'Which subreddits does it use?', a: 'The ones that actually surface for your product context, not a hand-picked list.' },
    ],
  },
  'brand-mention-checker': {
    apiId: 'reddit',
    related: ['reddit-research', 'switch-signals', 'youtube-comment-analyzer'],
    faq: [
      { q: 'What is searched?', a: 'Public Reddit posts and comments naming your brand or keyword, each returned with a direct source link.' },
      { q: 'How far back does it reach?', a: 'It returns the recent public sample for your term and prints when those rows were retrieved.' },
      { q: 'Can I monitor this continuously?', a: 'Not here. Scheduled monitoring is the Reddit API, billed per request in USD.' },
    ],
  },
  'switch-signals': {
    apiId: 'reddit',
    related: ['brand-mention-checker', 'reddit-research', 'reddit-marketing'],
    faq: [
      { q: 'What is a switch signal?', a: 'A public post where someone describes leaving, replacing, or hunting an alternative to a named competitor.' },
      { q: 'Is the intent guessed?', a: 'Phrasing is matched against your competitor term, and every row keeps its Reddit link so you judge it.' },
      { q: 'Which competitors work?', a: 'Any name people actually type. The tool searches the term you give it, not a fixed vendor list.' },
    ],
  },
  'ask-social-data': {
    apiId: 'social',
    related: ['profile-viewer', 'reddit-research', 'facebook-ad-library'],
    faq: [
      { q: 'How does it work?', a: 'Your plain-language question is routed to one constrained social-data operation, and the tool shows which operation ran.' },
      { q: 'What if the question does not map?', a: 'You are told that, instead of being handed a fabricated answer.' },
      { q: 'Why show the operation?', a: 'So the answer can be reproduced directly against the same API call.' },
    ],
  },
  'facebook-ad-library': {
    apiId: 'facebook',
    related: ['switch-signals', 'brand-mention-checker', 'ask-social-data'],
    faq: [
      { q: 'What can I search by?', a: 'Brand or keyword, filtered by country and active status, across public ads in the Meta ad library.' },
      { q: 'Is the result shape consistent?', a: 'Yes. Ads are normalized to the same fields, each linked back to its public ad.' },
      { q: 'Do I need a Meta account?', a: 'No. The library data is public and no login is required here.' },
    ],
  },
  'amazon-review-signals': {
    apiId: 'amazon',
    related: ['app-review-analyzer', 'brand-mention-checker', 'stock-comparison'],
    faq: [
      { q: 'Does it detect fake reviews?', a: 'No. It surfaces rating mix, verified-purchase share, timing clusters, and repeated wording, and leaves the verdict to you.' },
      { q: 'Which reviews are read?', a: 'The public review sample returned for the product, with the retrieved row count stated.' },
      { q: 'Why does repeated wording matter?', a: 'Identical phrasing across reviews is a signal worth a look, not proof of anything.' },
    ],
  },
  'app-review-analyzer': {
    apiId: 'appstore',
    related: ['amazon-review-signals', 'brand-mention-checker', 'stock-comparison'],
    faq: [
      { q: 'Which stores are covered?', a: 'App Store and Google Play, normalized to one shape so rating distributions compare side by side.' },
      { q: 'What does it show?', a: 'Rating spread, recurring review words, and weekly review volume from the public sample it retrieved.' },
      { q: 'Can I analyze a competitor app?', a: 'Yes. Store reviews are public, so no ownership of the listing is needed.' },
    ],
  },
  'stock-comparison': {
    apiId: 'yahoo-finance',
    related: ['amazon-review-signals', 'app-review-analyzer', 'creator-earnings-calculator'],
    faq: [
      { q: 'Which facts are compared?', a: 'Price, day range, 52-week range, volume, market cap, and market state for the exact tickers you enter.' },
      { q: 'Is this investment advice?', a: 'No. It reports quote fields as returned and makes no recommendation.' },
      { q: 'How fresh are the quotes?', a: 'Each run carries its retrieval time, so you can see how current the sample is.' },
    ],
  },
  'naver-blog-analytics': {
    apiId: 'naver',
    related: ['reddit-seo-finder', 'brand-mention-checker', 'reddit-research'],
    faq: [
      { q: 'Can I see my visitor counts?', a: 'No. Visitor and page-view stats are owner-only inside Naver Analytics. This measures the public result sample.' },
      { q: 'What does it measure?', a: 'Visible competition, blogger concentration, result freshness, and your rank among the rows that were returned.' },
      { q: 'Do I need a Naver blog?', a: 'No. The blog ID is optional and only used to locate your own rank.' },
    ],
  },
  'reddit-seo-finder': {
    apiId: 'reddit',
    related: ['switch-signals', 'brand-mention-checker', 'reddit-research'],
    faq: [
      { q: 'What does it find?', a: 'Reddit threads already ranking on Google for buyer-intent keywords related to your site.' },
      { q: 'Where do the keywords come from?', a: 'From your public site, and each thread keeps the search result and permalink it came from.' },
      { q: 'Why target Reddit threads?', a: 'They already hold the ranking, so joining one is faster than outranking it.' },
    ],
  },
  'x-non-followers': {
    apiId: 'x-twitter',
    related: ['instagram-non-followers', 'profile-viewer', 'username-checker'],
    faq: [
      { q: 'Do I have to connect my account?', a: 'No. The check runs against public following and follower samples for the handle you type.' },
      { q: 'Is the list complete?', a: 'It is a bounded sample, and the coverage actually reached is stated with the result.' },
      { q: 'Does anyone get notified?', a: 'No. Nothing is followed, unfollowed, or written to the account.' },
    ],
  },
  'tiktok-transcript': {
    apiId: 'tiktok',
    related: ['youtube-transcript', 'instagram-transcript', 'social-transcript'],
    faq: [
      { q: 'Which links work?', a: 'Any public TikTok video URL. Paste it and the spoken-word transcript comes back.' },
      { q: 'Can I download the text?', a: 'Yes. Copy it, download it, or pipe it straight into your own workflow.' },
      { q: 'What if the video has no speech?', a: 'You get an empty result with the sources checked, rather than an invented transcript.' },
    ],
  },
  'youtube-transcript': {
    apiId: 'youtube',
    related: ['youtube-channel-transcripts', 'tiktok-transcript', 'social-transcript'],
    faq: [
      { q: 'Does the video need captions?', a: 'It returns the spoken-word transcript available for that video. When none exists, the result says so.' },
      { q: 'Is there a length limit?', a: 'Full-length videos work, and the whole returned transcript is shown to copy or download.' },
      { q: 'Do I need a YouTube login?', a: 'No. Public videos only, no account, and no browser extension.' },
    ],
  },
  'instagram-transcript': {
    apiId: 'instagram',
    related: ['tiktok-transcript', 'youtube-transcript', 'social-transcript'],
    faq: [
      { q: 'Which posts work?', a: 'Public Instagram video posts and Reels. Private accounts are not retrieved.' },
      { q: 'What do I get?', a: 'The spoken-word transcript, ready to copy, download, or feed into your own workflow.' },
      { q: 'Do I need to log in?', a: 'No. There is no login, no extension, and no connected account.' },
    ],
  },
};
